import { TRADE_DEFINITIONS } from './tradeDefinitions';

export type OrgSettingsTradeDefinition = {
  key: string;
  displayName: string;
  category: string;
};

export type OrgSettingsTradeSettings = {
  displayName?: string;
  category?: string;
  description?: string;
  isEnabled?: boolean;
};

export const ORG_SETTINGS_TRADE_CATEGORIES = [
  'Site & Structure',
  'Mechanical, Electrical & Plumbing',
  'Interior',
  'Exterior',
  'Specialty Services',
  'Other',
] as const;

const TRADE_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  mason: 'Masonry',
  structuralEngineer: 'Structural Engineering',
  electrician: 'Electrical',
  plumber: 'Plumbing',
  cabinets: 'Cabinetry',
  tiler: 'Tiling',
  landscaper: 'Landscaping',
  poolVendor: 'Pool & Spa',
  cleaner: 'Cleaning',
  locksmithRekey: 'Locksmithing',
  appliances: 'Appliances',
};

const TRADE_KEYS_BY_CATEGORY: Record<(typeof ORG_SETTINGS_TRADE_CATEGORIES)[number], string[]> = {
  'Site & Structure': ['general', 'concrete', 'excavation', 'mason', 'steel', 'structuralEngineer', 'foundation'],
  'Mechanical, Electrical & Plumbing': ['electrician', 'hvac', 'plumber', 'septic'],
  Interior: ['carpenter', 'flooring', 'tiler', 'painting', 'insulation', 'glazier', 'windows', 'countertops', 'cabinets', 'appliances'],
  Exterior: ['roofer', 'landscaper', 'garageDoor', 'poolVendor', 'resurfacing'],
  'Specialty Services': ['pestControl', 'cleaner', 'locksmithRekey'],
  Other: ['inHouse'],
};

const TRADE_CATEGORY_BY_KEY = Object.fromEntries(
  Object.entries(TRADE_KEYS_BY_CATEGORY).flatMap(([category, tradeKeys]) => tradeKeys.map(tradeKey => [tradeKey, category])),
);

const getTradeDisplayName = (tradeKey: string, fallbackLabel: string) => TRADE_DISPLAY_NAME_OVERRIDES[tradeKey] ?? fallbackLabel;

const getTradeCategory = (tradeKey: string) => TRADE_CATEGORY_BY_KEY[tradeKey] ?? 'Other';

export const ORG_SETTINGS_TRADE_DEFINITIONS: readonly OrgSettingsTradeDefinition[] = TRADE_DEFINITIONS.map(({ slug, label }) => ({
  key: slug,
  displayName: getTradeDisplayName(slug, label),
  category: getTradeCategory(slug),
}));

const ORG_SETTINGS_TRADE_DEFINITIONS_BY_KEY = new Map(
  ORG_SETTINGS_TRADE_DEFINITIONS.map(tradeDefinition => [tradeDefinition.key, tradeDefinition]),
);

const trimOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
};

const isTradeSettingsObject = (
  value: unknown,
): value is OrgSettingsTradeSettings => !!value && typeof value === 'object' && !Array.isArray(value);

const getConfiguredTrade = (tradeRecord: unknown, tradeKey: string): unknown => {
  if (!tradeRecord || typeof tradeRecord !== 'object' || Array.isArray(tradeRecord)) return undefined;
  return (tradeRecord as Record<string, unknown>)[tradeKey];
};

const getNormalizedTradeOverride = (
  tradeKey: string,
  tradeValue: unknown,
): OrgSettingsTradeSettings => {
  if (!isTradeSettingsObject(tradeValue)) return {};

  const tradeDefinition = ORG_SETTINGS_TRADE_DEFINITIONS_BY_KEY.get(tradeKey);
  const displayName = trimOptionalString(tradeValue.displayName);
  const description = trimOptionalString(tradeValue.description);

  return {
    ...(displayName && displayName !== tradeDefinition?.displayName ? { displayName } : {}),
    ...(description ? { description } : {}),
    ...(tradeValue.isEnabled === false ? { isEnabled: false } : {}),
  };
};

const getResolvedTradeSettings = (
  tradeDefinition: OrgSettingsTradeDefinition,
  tradeValue: unknown,
): OrgSettingsTradeSettings => {
  const normalizedTradeOverride = getNormalizedTradeOverride(tradeDefinition.key, tradeValue);

  return {
    displayName: normalizedTradeOverride.displayName ?? tradeDefinition.displayName,
    category: tradeDefinition.category,
    ...(normalizedTradeOverride.description ? { description: normalizedTradeOverride.description } : {}),
    ...(normalizedTradeOverride.isEnabled === false ? { isEnabled: false } : {}),
  };
};

/**
 * Returns the full effective org-settings trade record.
 *
 * Every standard trade is always present, using default labels/categories when the
 * org has not provided a custom label or disabled state. Unknown trade keys are ignored.
 */
export const getOrgSettingsTradeRecordOrDefaults = (
  tradeRecord: unknown,
): Record<string, OrgSettingsTradeSettings> =>
  Object.fromEntries(
    ORG_SETTINGS_TRADE_DEFINITIONS.map(tradeDefinition => [
      tradeDefinition.key,
      getResolvedTradeSettings(
        tradeDefinition,
        getConfiguredTrade(tradeRecord, tradeDefinition.key),
      ),
    ]),
  );

/**
 * Returns the minimal persisted org-settings trade record.
 *
 * Only non-default custom labels, descriptions, and disabled flags are stored.
 * Unknown trade keys are dropped so the canonical trade list remains disable-only.
 */
export const getStoredOrgSettingsTradeRecord = (
  tradeRecord: unknown,
): Record<string, OrgSettingsTradeSettings> =>
  Object.fromEntries(
    ORG_SETTINGS_TRADE_DEFINITIONS
      .map(tradeDefinition =>
        [
          tradeDefinition.key,
          getNormalizedTradeOverride(
            tradeDefinition.key,
            getConfiguredTrade(tradeRecord, tradeDefinition.key),
          ),
        ] as const
      )
      .filter(([, trade]) => Object.keys(trade).length > 0),
  );

/**
 * Returns the default effective trade record for org settings.
 */
export const getDefaultOrgSettingsTrades = (): Record<string, OrgSettingsTradeSettings> => getOrgSettingsTradeRecordOrDefaults(undefined);
